"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Adventure = void 0;
const npcCharacter_1 = require("../utils/npcCharacter");
const AdventureHeader_1 = require("./AdventureHeader");
const AdventureScene_1 = require("./AdventureScene");
const AdventureItem_1 = require("./AdventureItem");
const AdventureStat_1 = require("./AdventureStat");
const analyticsScriptSnapshotPolicy_1 = require("../engine/analyticsScriptSnapshotPolicy");
const sharedDataRichText_1 = require("../utils/sharedDataRichText");
class Adventure {
    constructor(data, providedId) {
        // Create header
        const headerData = {
            id: providedId || this.generateId(data.title),
            title: data.title,
            description: data.description,
            subAdventures: data.subAdventures, // Sub-adventures metadata
            characters: data.characters || [],
            players: data.players || [], // Include player definitions
            style: data.style,
            titleImageId: data.titleImageId,
            bannerId: data.bannerId,
            tickIntervalMs: data.tickIntervalMs,
            tags: data.tags && data.tags.length > 0 ? [...data.tags] : undefined,
            hideHeaderForGuests: data.hideHeaderForGuests,
            library: data.library
        };
        this.header = new AdventureHeader_1.AdventureHeader(headerData);
        // Validate scenes for duplicates
        const sceneIds = (data.scenes || []).map(s => s.id);
        const duplicateSceneIds = sceneIds.filter((id, index) => sceneIds.indexOf(id) !== index);
        if (duplicateSceneIds.length > 0) {
            throw new Error(`Duplicate scene IDs detected: ${[...new Set(duplicateSceneIds)].join(', ')}`);
        }
        // Create scenes map
        this.scenesMap = new Map();
        (data.scenes || []).forEach(scene => {
            this.scenesMap.set(scene.id, new AdventureScene_1.AdventureScene(scene));
        });
        // Create items map
        this.itemsMap = new Map();
        (data.items || []).forEach(item => {
            const itemData = {
                tag: item.tag,
                name: item.name,
                description: item.description,
                iconId: item.iconId
            };
            this.itemsMap.set(item.tag, new AdventureItem_1.AdventureItem(itemData));
        });
        // Create stats map
        this.statsMap = new Map();
        (data.stats || []).forEach(stat => {
            const statData = {
                name: stat.name,
                description: stat.description
            };
            this.statsMap.set(stat.name, new AdventureStat_1.AdventureStat(statData));
        });
        // Legacy properties for backward compatibility
        this.title = data.title;
        this.description = data.description;
        this.characters = data.characters || [];
        this.players = data.players || [];
        this.scenes = data.scenes || [];
        this.items = data.items || [];
        this.locations = data.locations || [];
        this.stats = data.stats || [];
        this.variables = data.variables || [];
        this.characterVariables = data.characterVariables || [];
        this.sharedVariables = data.sharedVariables || [];
        this.sharedVariableScope = data.sharedVariableScope;
        this.activityTypes = data.activityTypes || [];
        this.activities = data.activities || [];
        this.entityTypes = data.entityTypes || [];
        this.entityDependentSceneIds = data.entityDependentSceneIds
            ? [...data.entityDependentSceneIds]
            : [];
        this.tickIntervalMs = data.tickIntervalMs;
        this.tags = data.tags ? [...data.tags] : undefined;
        this.hideHeaderForGuests = data.hideHeaderForGuests;
        this.library = data.library;
        this.scripts = data.scripts;
        const analyticsUsage = (0, analyticsScriptSnapshotPolicy_1.computeAdventureAnalyticsSnapshotUsage)(data);
        this.analyticsSnapshotSceneIds = analyticsUsage.sceneIds;
        this.analyticsSnapshotSharedContentUses = analyticsUsage.sharedContentUsesAnalytics;
        this.analyticsSnapshotValidSceneIds = new Set(this.scenesMap.keys());
        const displayNameUsage = (0, analyticsScriptSnapshotPolicy_1.computeAdventureAnalyticsDisplayNameUsage)(data);
        this.analyticsDisplayNameSceneIds = displayNameUsage.sceneIds;
        this.analyticsDisplayNameSharedContentUses = displayNameUsage.sharedContentUsesDisplayName;
        this.adventureUsesAnalyticsDisplayName =
            displayNameUsage.sharedContentUsesDisplayName || displayNameUsage.sceneIds.size > 0;
    }
    /** True when any scene or shared adventure content references analytics display-name variables or alias helpers. */
    usesAnalyticsDisplayName() {
        return this.adventureUsesAnalyticsDisplayName;
    }
    /**
     * Whether `AnalyticsScriptService.refreshSnapshot()` should run before interpolating/displaying `sceneId`.
     * Conservative when shared adventure content (items, locations, etc.) references analytics snapshot APIs.
     */
    shouldRefreshAnalyticsSnapshotForSceneDisplay(sceneId) {
        return (0, analyticsScriptSnapshotPolicy_1.shouldRefreshAnalyticsSnapshotForSceneDisplay)({
            sharedContentUsesAnalytics: this.analyticsSnapshotSharedContentUses,
            sceneIdsUsingAnalytics: this.analyticsSnapshotSceneIds,
            sceneId,
        });
    }
    /**
     * Whether `AnalyticsScriptService.refreshSnapshot()` should run before processing an option selection,
     * based on static scanning of the adventure plus the selected option fields and target shape.
     */
    shouldRefreshAnalyticsSnapshotForPendingOptionSelection(args) {
        return (0, analyticsScriptSnapshotPolicy_1.shouldRefreshAnalyticsSnapshotForPendingOptionSelection)({
            validSceneIds: this.analyticsSnapshotValidSceneIds,
            sceneIdsUsingAnalytics: this.analyticsSnapshotSceneIds,
            sharedContentUsesAnalytics: this.analyticsSnapshotSharedContentUses,
            currentSceneId: args.currentSceneId,
            selectedOption: args.selectedOption,
            rawTarget: args.rawTarget,
            interpolatedNonScriptTarget: args.interpolatedNonScriptTarget,
        });
    }
    /**
     * Whether `AnalyticsClient.getDisplayName()` should run before interpolating/displaying `sceneId`.
     */
    shouldSyncAnalyticsDisplayNameForSceneDisplay(sceneId) {
        if (!this.adventureUsesAnalyticsDisplayName) {
            return false;
        }
        return (0, analyticsScriptSnapshotPolicy_1.shouldSyncAnalyticsDisplayNameForSceneDisplay)({
            sharedContentUsesDisplayName: this.analyticsDisplayNameSharedContentUses,
            sceneIdsUsingDisplayName: this.analyticsDisplayNameSceneIds,
            sceneId,
        });
    }
    /**
     * Whether `AnalyticsClient.getDisplayName()` should run before processing an option selection.
     */
    shouldSyncAnalyticsDisplayNameForPendingOptionSelection(args) {
        if (!this.adventureUsesAnalyticsDisplayName) {
            return false;
        }
        return (0, analyticsScriptSnapshotPolicy_1.shouldSyncAnalyticsDisplayNameForPendingOptionSelection)({
            validSceneIds: this.analyticsSnapshotValidSceneIds,
            sceneIdsUsingDisplayName: this.analyticsDisplayNameSceneIds,
            sharedContentUsesDisplayName: this.analyticsDisplayNameSharedContentUses,
            currentSceneId: args.currentSceneId,
            selectedOption: args.selectedOption,
            rawTarget: args.rawTarget,
            interpolatedNonScriptTarget: args.interpolatedNonScriptTarget,
        });
    }
    generateId(title) {
        return title.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
    }
    isValidPortraitId(portraitId) {
        // Portrait IDs can be:
        // 1. Simple format: lowercase alphanumeric with underscores (e.g., "art_director", "generic")
        // 2. Namespaced format: {adventure-id}:{portrait-id} (e.g., "styling-exploration:art_director")
        //    - adventure-id: lowercase alphanumeric with hyphens
        //    - portrait-id: lowercase alphanumeric with underscores
        // Check for namespaced format first
        if (portraitId.includes(':')) {
            const parts = portraitId.split(':');
            if (parts.length !== 2) {
                return false; // Invalid format (multiple colons)
            }
            const [adventureId, localId] = parts;
            // Adventure ID format: lowercase with hyphens
            const adventureIdValid = /^[a-z0-9-]+$/.test(adventureId);
            // Portrait ID format: lowercase with underscores
            const localIdValid = /^[a-z0-9_]+$/.test(localId);
            return adventureIdValid && localIdValid;
        }
        // Simple format: lowercase alphanumeric with underscores
        return /^[a-z0-9_]+$/.test(portraitId);
    }
    // New methods for accessing individual components (Phase 1)
    getHeader() {
        return this.header;
    }
    getScene(id) {
        return this.scenesMap.get(id);
    }
    getItem(tag) {
        return this.itemsMap.get(tag);
    }
    getLocation(tag) {
        return this.locations.find(loc => loc.tag === tag);
    }
    getStat(name) {
        return this.statsMap.get(name);
    }
    // Legacy methods for backward compatibility
    getCharacter(name) {
        return this.header.getCharacter(name);
    }
    /** Characters that are part of the NPC update system (see `isNpcDefinition`). */
    getNpcDefinitions() {
        return (this.characters || []).filter(npcCharacter_1.isNpcDefinition);
    }
    getLegacyScene(id) {
        return this.scenes.find(scene => scene.id === id);
    }
    getStartingScene(characterName) {
        const character = this.getCharacter(characterName);
        if (!character)
            return undefined;
        return this.getLegacyScene(character.startingScene);
    }
    validate() {
        const errors = [];
        // Validate characters
        const characterNames = this.header.getCharacterNames();
        const duplicateCharacters = characterNames.filter((name, index) => characterNames.indexOf(name) !== index);
        if (duplicateCharacters.length > 0) {
            errors.push(`Duplicate character names: ${duplicateCharacters.join(', ')}`);
        }
        // Check that all character starting scenes exist
        for (const character of this.header.characters) {
            if (!this.getScene(character.startingScene)) {
                errors.push(`Character '${character.name}' has invalid starting scene: '${character.startingScene}'`);
            }
            // Validate portrait ID
            if (!character.portraitId) {
                // This shouldn't happen as parser defaults to 'generic', but check anyway
                errors.push(`Character '${character.name}' is missing portraitId (should default to 'generic')`);
            }
            else if (character.portraitId !== 'generic' &&
                !(character.portraitId?.startsWith('${') && character.portraitId.endsWith('}')) &&
                !this.isValidPortraitId(character.portraitId)) {
                // Warning for invalid format (not an error, as it doesn't break gameplay)
                errors.push(`Warning: Character '${character.name}' has portrait ID '${character.portraitId}' that doesn't follow naming convention (use lowercase with underscores)`);
            }
        }
        // Validate items - check for duplicate item tags
        const itemTags = this.items.map(item => item.tag);
        const duplicateItemTags = itemTags.filter((tag, index) => itemTags.indexOf(tag) !== index);
        if (duplicateItemTags.length > 0) {
            errors.push(`Duplicate item tags: ${duplicateItemTags.join(', ')}`);
        }
        // Collect all referenced item tags from scenes
        const referencedItemTags = new Set();
        for (const [sceneId, scene] of this.scenesMap) {
            // Collect item tags from auto effects (script format)
            if (scene.autoEffect) {
                // Match addItem("tag") or removeItem("tag")
                const itemMatches = scene.autoEffect.matchAll(/(?:add|remove)Item\s*\(\s*["']([^"']+)["']\s*\)/g);
                for (const match of itemMatches) {
                    referencedItemTags.add(match[1]);
                }
            }
            // Collect item tags from option conditions and effects (script format)
            for (const option of scene.getAllOptions()) {
                // Extract item tags from condition scripts
                if (option.condition) {
                    // Match hasItem("tag") or !hasItem("tag")
                    const itemMatches = option.condition.matchAll(/hasItem\s*\(\s*["']([^"']+)["']\s*\)/g);
                    for (const match of itemMatches) {
                        referencedItemTags.add(match[1]);
                    }
                }
                // Extract item tags from effect scripts
                if (option.effect) {
                    // Match addItem("tag") or removeItem("tag")
                    const itemMatches = option.effect.matchAll(/(?:add|remove)Item\s*\(\s*["']([^"']+)["']\s*\)/g);
                    for (const match of itemMatches) {
                        referencedItemTags.add(match[1]);
                    }
                }
            }
        }
        // Validate that all referenced item tags have definitions
        for (const itemTag of referencedItemTags) {
            if (!this.getItem(itemTag)) {
                errors.push(`Item tag '${itemTag}' is referenced but not defined in Items section`);
            }
        }
        // Validate scenes
        for (const [sceneId, scene] of this.scenesMap) {
            // Validate scene options
            const sceneErrors = scene.validateOptions();
            errors.push(...sceneErrors.map(error => `Scene '${sceneId}': ${error}`));
            // Check that all option targets exist (except FINISH, LEAVE, MAIN_MENU, QUIT_ADVENTURE, RETURN_TO_ADVENTURE, START_*, JOIN_*, LOGGED_IN, SIGN_OUT, and DELETE_ACCOUNT which are special)
            // Skip validation for variable references (targets starting with ${) and script targets (containing ''' delimiters)
            for (const option of scene.getAllOptions()) {
                // Check if target is a script target (contains ''' delimiters for script-based target evaluation)
                // Script targets are evaluated at runtime, so we can't validate the target scene until then
                const isScriptTarget = option.target.includes("'''");
                // Check if target is a script expression with other syntax (legacy check for compatibility)
                const isScriptExpression = option.target.includes('+') || option.target.includes('getVariable') || option.target.includes('getAdventureInvite');
                const isSpecialTarget = option.target === 'FINISH' ||
                    option.target === 'LEAVE' ||
                    option.target === 'MAIN_MENU' ||
                    option.target === 'QUIT_ADVENTURE' ||
                    option.target === 'LOGGED_IN' ||
                    option.target === 'RETURN_TO_ADVENTURE' ||
                    option.target === 'SIGN_OUT' ||
                    option.target === 'DELETE_ACCOUNT' ||
                    option.target.startsWith('START_ADVENTURE:') ||
                    option.target.startsWith('START_OR_RESUME_ADVENTURE:') ||
                    option.target.startsWith('JOIN_ADVENTURE:') ||
                    (isScriptExpression && (option.target.includes('START_ADVENTURE') || option.target.includes('START_OR_RESUME_ADVENTURE') || option.target.includes('JOIN_ADVENTURE') || option.target.includes('RETURN_TO_ADVENTURE') || option.target.includes('SIGN_OUT') || option.target.includes('DELETE_ACCOUNT')));
                // Skip validation for script targets (evaluated at runtime), special targets, variable interpolation, and script expressions
                if (!isSpecialTarget && !option.target.startsWith('${') && !isScriptTarget && !isScriptExpression && !this.getScene(option.target)) {
                    errors.push(`Scene '${sceneId}' has option targeting non-existent scene: '${option.target}'`);
                }
            }
        }
        // Validate: adventure must offer an explicit exit — FINISH, LEAVE, or RETURN_TO_ADVENTURE (hub / return flows)
        let hasExitOption = false;
        for (const [sceneId, scene] of this.scenesMap) {
            if (scene.hasFinishOption() || scene.hasLeaveOption() || scene.hasReturnToAdventureOption()) {
                hasExitOption = true;
                break;
            }
        }
        if (!hasExitOption) {
            errors.push('Adventure must have at least one scene with an option with Target: FINISH, Target: LEAVE, or Target: RETURN_TO_ADVENTURE');
        }
        errors.push(...(0, sharedDataRichText_1.validateSharedVariablesAndSharedData)(this));
        return errors;
    }
}
exports.Adventure = Adventure;
//# sourceMappingURL=Adventure.js.map